/**
 * Normalizes branch/ref input for Acceptance Review.
 * Accepts plain branch names, GitHub URLs, and owner/repo/tree/branch paths.
 * Returns the extracted branch name or an error.
 *
 * @param {unknown} raw
 * @returns {{ ok: true, branch: string } | { ok: false, message: string }}
 */
export function normalizeBranchInput(raw) {
  if (typeof raw !== "string") return { ok: false, message: "Branch is required" };
  let s = raw.trim();
  if (!s) return { ok: false, message: "Branch is required" };

  const q = s.indexOf("?");
  const h = s.indexOf("#");
  let cut = s.length;
  if (q >= 0) cut = Math.min(cut, q);
  if (h >= 0) cut = Math.min(cut, h);
  s = s.slice(0, cut).trim().replace(/\/+$/, "");

  // Full GitHub URL: https://github.com/owner/repo/tree/branch/path
  const githubTreeMatch = s.match(
    /^(?:https?:\/\/)?(?:www\.)?github\.com\/[^/]+\/[^/]+\/tree\/(.+)$/i
  );
  if (githubTreeMatch) {
    const branch = githubTreeMatch[1].replace(/\/+$/, "");
    return branch
      ? { ok: true, branch }
      : { ok: false, message: "Could not extract branch from URL" };
  }

  // owner/repo/tree/branch (no protocol)
  const relativeTreeMatch = s.match(/^[^/]+\/[^/]+\/tree\/(.+)$/);
  if (relativeTreeMatch) {
    const branch = relativeTreeMatch[1].replace(/\/+$/, "");
    return branch
      ? { ok: true, branch }
      : { ok: false, message: "Could not extract branch from path" };
  }

  // GitHub URL without /tree/ — repo URL, not a branch
  if (/^(?:https?:\/\/)?(?:www\.)?github\.com\//i.test(s)) {
    if (/^(?:https?:\/\/)?(?:www\.)?github\.com\/[^/]+\/[^/]+\/?$/i.test(s)) {
      return {
        ok: false,
        message:
          "That looks like a repository URL, not a branch. Paste a branch URL (github.com/owner/repo/tree/branch-name) or just the branch name.",
      };
    }
    return {
      ok: false,
      message:
        "Could not extract a branch from that GitHub URL. Paste a /tree/ branch URL or just the branch name.",
    };
  }

  // Non-GitHub URL
  if (/^https?:\/\//i.test(s)) {
    return {
      ok: false,
      message:
        "Only GitHub URLs are supported. Paste a branch name or a github.com/owner/repo/tree/branch URL.",
    };
  }

  // Control characters
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) {
      return { ok: false, message: "Branch name contains invalid characters." };
    }
  }

  if (s.length > 256) {
    return { ok: false, message: "Branch name is too long." };
  }

  return { ok: true, branch: s };
}
